import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { ConfirmDialog, DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import dayjs from "utils/dayjs";
import WebhookEndpointStatus from "enums/WebhookEndpointStatus";
import type { WebhookEndpoint } from "hooks/useWebhookEndpoints";
import { Stack } from "components/layout/reusableLayouts";
import WebhookEndpointStatusChip from "./WebhookEndpointStatusChip";

interface WebhookEndpointsTableProps {
  endpoints: WebhookEndpoint[];
  loading: boolean;
  loadError?: boolean;
  onEdit: (endpoint: WebhookEndpoint) => void;
  onSetPaused: (endpoint: WebhookEndpoint, paused: boolean) => Promise<void>;
  // Both reject after reporting a failure, so the confirmation stays open to retry.
  onRotate: (endpoint: WebhookEndpoint) => Promise<void>;
  onDelete: (endpoint: WebhookEndpoint) => Promise<void>;
}

type Confirming = { kind: "rotate" | "delete"; endpoint: WebhookEndpoint };

const WebhookEndpointsTable: React.FC<WebhookEndpointsTableProps> = ({
  endpoints,
  loading,
  loadError,
  onEdit,
  onSetPaused,
  onRotate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  // Stops a second click on Confirm from sending a second rotation.
  const [busy, setBusy] = useState(false);

  const columns: DataColumn<WebhookEndpoint>[] = useMemo(
    () => [
      {
        id: "url",
        header: t("Endpoint URL"),
        cell: (endpoint) => ({
          content: (
            <Typography variant="label" noWrap>
              {endpoint.url}
            </Typography>
          ),
          caption: endpoint.id,
        }),
      },
      {
        id: "events",
        header: t("Events"),
        cell: (endpoint) => endpoint.events.length.toLocaleString(),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (endpoint) => (
          <WebhookEndpointStatusChip status={endpoint.status} />
        ),
      },
      {
        id: "created",
        header: t("Created"),
        cell: (endpoint) => dayjs(endpoint.createdAt).format("MMM D, YYYY"),
      },
    ],
    [t],
  );

  const rowActions = (endpoint: WebhookEndpoint): RowAction[] => {
    const paused = endpoint.status === WebhookEndpointStatus.PAUSED;
    return [
      { label: t("Edit"), onClick: () => onEdit(endpoint) },
      {
        label: paused ? t("Resume") : t("Pause"),
        onClick: () => {
          onSetPaused(endpoint, !paused).catch(() => undefined);
        },
      },
      {
        label: t("Rotate secret"),
        danger: true,
        onClick: () => setConfirming({ kind: "rotate", endpoint }),
      },
      {
        label: t("Delete"),
        danger: true,
        onClick: () => setConfirming({ kind: "delete", endpoint }),
      },
    ];
  };

  const confirm = async () => {
    if (!confirming || busy) return;
    setBusy(true);
    try {
      if (confirming.kind === "rotate") await onRotate(confirming.endpoint);
      else await onDelete(confirming.endpoint);
      setConfirming(null);
    } catch {
      // The caller has already reported it; staying open lets it be retried.
    } finally {
      setBusy(false);
    }
  };

  const rotating = confirming?.kind === "rotate";

  return (
    <>
      <DataTable<WebhookEndpoint>
        columns={columns}
        rows={endpoints}
        getRowKey={(endpoint) => endpoint.id}
        loading={loading}
        errorState={
          loadError ? (
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-danger)" }}
            >
              {t("WebhookListLoadError")}
            </Typography>
          ) : undefined
        }
        emptyState={
          <Stack gap="var(--space-1)">
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {t("No webhook endpoints yet")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("WebhookEmptyStateSubtitle")}
            </Typography>
          </Stack>
        }
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        stickyHeader
      />

      <ConfirmDialog
        open={!!confirming}
        tone="danger"
        title={rotating ? t("Rotate signing secret") : t("Delete endpoint")}
        message={
          <Trans
            i18nKey={rotating ? "WebhookRotateConfirm" : "WebhookDeleteConfirm"}
            values={{ url: confirming?.endpoint.url }}
            components={{ strong: <strong /> }}
          />
        }
        confirmLabel={rotating ? t("Rotate") : t("Delete")}
        cancelLabel={t("No")}
        busy={busy}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) setConfirming(null);
        }}
      />
    </>
  );
};

export default WebhookEndpointsTable;
